import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../../components/default/DefaultCard';
import LazyLoading from '../../../components/LazyLoading';
import { toast } from 'react-toastify';
import { Tab, Tabs } from 'react-bootstrap';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import useNavigationPage from '../../../hooks/useNavigationPage';
import { AppContext } from '../../../contexts/AppContext';
import usePackingOrderService from '../../../app/services/hooks/usePackingOrderService';
import useProductService from '../../../app/services/hooks/useProductService';
import useUnitService from '../../../app/services/hooks/useUnitService';
import useCenterService from '../../../app/services/hooks/useCenterService';
import useWarehouseService from '../../../app/services/hooks/useWarehouseService';
import {
  PackingOrderDataForm,
  defaultPackingOrderDataForm,
  PackingOrderOutputProductForm,
  PackingOrderInputProductForm,
  PackingOrderRequiredStockGroup,
  EmpacarDataResponse,
  PackingOrderProducedBatchForm
} from '../../../app/models/PackingOrder';
import PackingOrderHeaderForm from '../components/forms/PackingOrderHeaderForm';
import PackingOrderOutputProductSection from '../components/sections/PackingOrderOutputProductSection';
import PackingOrderInputProductSection from '../components/sections/PackingOrderInputProductSection';
import OrderIncidentsTab from '../components/tables/OrderIncidentsTab';
import EmpacarModal from '../components/EmpacarModal';

type SelectOpt = { id: number | string; name: string };

type RouterParams = { id: string };

const PackingOrderEditPage = () => {
  const { id } = useParams<RouterParams>();
  const packingOrderId = parseInt(id || '0', 10);
  const { showLoading, hideLoading } = useContext(AppContext);
  const { navigationPage } = useNavigationPage();
  const {
    editPackingOrder,
    updatePackingOrder,
    getOutputProducts,
    getInputProducts,
    getRequiredStock,
    getEmpacarData,
    executeEmpacar,
    getProducedBatches,
    fetchingEditPackingOrder,
    fetchingUpdatePackingOrder,
    fetchingEmpacarData,
    fetchingEmpacarExecute,
    fetchingProducedBatches
  } = usePackingOrderService();
  const { getProducts } = useProductService();
  const { getUnits } = useUnitService();
  const { getCenters } = useCenterService();
  const { getWarehouses } = useWarehouseService();

  const [packingOrder, setPackingOrder] = useState<PackingOrderDataForm>(defaultPackingOrderDataForm);
  const [societies, setSocieties] = useState<SelectOpt[]>([]);
  const [statusChoices, setStatusChoices] = useState<SelectOpt[]>([]);
  const [centers, setCenters] = useState<SelectOpt[]>([]);
  const [warehouses, setWarehouses] = useState<SelectOpt[]>([]);
  const [errorFields, setErrorFields] = useState<any>();
  const [outputProducts, setOutputProducts] = useState<PackingOrderOutputProductForm[]>([]);
  const [inputProducts, setInputProducts] = useState<PackingOrderInputProductForm[]>([]);
  const [requiredStock, setRequiredStock] = useState<PackingOrderRequiredStockGroup[]>([]);
  const [producedBatches, setProducedBatches] = useState<PackingOrderProducedBatchForm[]>([]);
  const [showEmpacarModal, setShowEmpacarModal] = useState(false);
  const [empacarData, setEmpacarData] = useState<EmpacarDataResponse | null>(null);
  const [products, setProducts] = useState<SelectOpt[]>([]);
  const [units, setUnits] = useState<SelectOpt[]>([]);
  const [reloadTick, setReloadTick] = useState(0);

  const breadcrumbs: BreadcrumbsItem[] = [
    { name: 'Órdenes de Empaque', url: '/packing-orders', isActive: false },
    { name: 'Editar #' + id, url: '/packing-orders/' + id + '/edit', isActive: true }
  ];

  useEffect(() => {
    if (!packingOrderId) return;
    loadPackingOrder();
    loadOutputProducts();
    loadInputProducts();
    loadRequiredStock();
    loadProducedBatches();
    loadProducts();
    loadUnits();
  }, [packingOrderId]);

  const loadPackingOrder = () => {
    showLoading?.('loading', 'Cargando orden...');
    editPackingOrder(packingOrderId, {
      onSuccess: (response: ServiceResponse) => {
        setPackingOrder(response.data.packing_order || defaultPackingOrderDataForm);
        setSocieties(response.data.societies || []);
        setStatusChoices(response.data.status_choices || []);
        setCenters(response.data.centers || []);
        setWarehouses(response.data.warehouses || []);
        hideLoading?.();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        toast.error(response.message);
        if (response.data?.to_dashboard) navigationPage('/packing-orders');
      }
    });
  };

  const formatDate = (d?: string | null) => {
    if (!d) return '';
    try {
      const date = new Date(d);
      return Number.isNaN(date.getTime()) ? (d as string) : date.toLocaleDateString('es-CL');
    } catch {
      return d as string;
    }
  };

  const formatIntQuantity = (value: any) => {
    if (value == null) return '0';
    const n = Number(value);
    return Number.isNaN(n) ? '0' : Math.trunc(n).toLocaleString('es-CL', { maximumFractionDigits: 0 });
  };

  const loadOutputProducts = () => {
    getOutputProducts(packingOrderId, {
      onSuccess: (response: ServiceResponse) => {
        setOutputProducts(response.data.output_products || []);
      }
    });
  };

  const loadInputProducts = () => {
    getInputProducts(packingOrderId, {
      onSuccess: (response: ServiceResponse) => {
        setInputProducts(response.data.input_products || []);
      }
    });
  };

  const loadRequiredStock = () => {
    getRequiredStock(packingOrderId, {
      onSuccess: (response: ServiceResponse) => {
        setRequiredStock(response.data.required_stock || []);
      }
    });
  };

  const loadProducedBatches = () => {
    getProducedBatches(packingOrderId, {
      onSuccess: (response: ServiceResponse) => {
        const list = response.data?.produced_batches ?? response.data?.data?.produced_batches ?? [];
        setProducedBatches(Array.isArray(list) ? list : []);
      },
      onError: (response: ServiceResponse) => {
        toast.error(response?.message || 'Error al cargar productos elaborados.');
      }
    });
  };

  const loadEmpacarData = () => {
    getEmpacarData(packingOrderId, {
      onSuccess: (response: ServiceResponse) => {
        setEmpacarData(response.data || null);
      },
      onError: (response: ServiceResponse) => {
        toast.error(response.message);
      }
    });
  };

  const handleEmpacarSubmit = (body: { movement_date?: string; outputs: Array<{ output_product_id: number; quantity_elaborated: number; consumptions: Array<{ batch_id: number; quantity: number }> }> }) => {
    showLoading?.('loading', 'Registrando empaque...');
    executeEmpacar(packingOrderId, body, {
      onSuccess: (response: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
        setShowEmpacarModal(false);
        loadOutputProducts();
        loadRequiredStock();
        loadProducedBatches();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
      }
    });
  };

  const loadProducts = () => {
    getProducts(
      { page: 1, per_page: 500, sort: 'id', order: 'asc' },
      {
        onSuccess: (response: ServiceResponse) => {
          const list = response.data?.products || [];
          setProducts(list.map((p: any) => ({
            id: p.id,
            name: p.name || p.sale_name || '',
            category_id: p.category_id ?? null,
            quality_id: p.quality_id ?? null,
            unit_id: p.unit_id ?? null,
          })));
        }
      }
    );
  };

  const loadUnits = () => {
    getUnits(
      { page: 1, per_page: 100, sort: 'id', order: 'asc' },
      {
        onSuccess: (response: ServiceResponse) => {
          const list = response.data?.units || response.data || [];
          setUnits(Array.isArray(list) ? list.map((u: any) => ({ id: u.id, name: u.name || '' })) : []);
        }
      }
    );
  };

  const onSocietyChangeForCenters = (societyId: number | undefined) => {
    if (!societyId) {
      setCenters([]);
      setWarehouses([]);
      return;
    }
    getCenters(
      { page: 1, per_page: 500, sort: 'name', order: 'asc', society_id: societyId },
      {
        onSuccess: (response: ServiceResponse) => {
          const list = response.data?.centers || [];
          setCenters(list.map((c: any) => ({ id: c.id, name: c.name || '' })));
          setWarehouses([]);
        }
      }
    );
  };

  const onCenterChangeForWarehouses = (centerId: number | undefined) => {
    if (!centerId) {
      setWarehouses([]);
      return;
    }
    getWarehouses(
      { page: 1, per_page: 500, sort: 'name', order: 'asc', center_id: centerId },
      {
        onSuccess: (response: ServiceResponse) => {
          const list = response.data?.warehouses || [];
          setWarehouses(list.map((w: any) => ({ id: w.id, name: w.name || '' })));
        }
      }
    );
  };

  const updateHeader = (overrideData?: PackingOrderDataForm) => {
    const payload = overrideData ?? packingOrder;
    showLoading?.('loading', 'Actualizando cabecera...');
    updatePackingOrder(packingOrderId, payload, {
      onSuccess: (response: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
        setErrorFields(undefined);
        loadPackingOrder();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoading?.();
        setErrorFields(errs.data);
      }
    });
  };

  if (!packingOrderId) {
    return null;
  }

  return (
    <>
      <Breadcrumbs pageSection={'Editar Orden de Empaque #' + id} breadcrumbs={breadcrumbs} />

      {/* Tabs superiores */}
      <Tabs defaultActiveKey="general" id="po-edit-top-tabs" className="mb-3">
        {/* === TAB 1: Datos Generales === */}
        <Tab eventKey="general" title="Datos Generales">
          <div className="row">
            {/* Información General de la Orden */}
            <div className="col-12 col-lg-8">
              <DefaultCard>
                <h5 className="mb-3">Información General de la Orden</h5>
                {!fetchingEditPackingOrder ? (
                  <PackingOrderHeaderForm
                    packingOrder={packingOrder}
                    setPackingOrder={setPackingOrder}
                    societies={societies}
                    centers={centers}
                    warehouses={warehouses}
                    onSocietyChange={onSocietyChangeForCenters}
                    onCenterChange={onCenterChangeForWarehouses}
                    statusChoices={statusChoices}
                    errorFields={errorFields}
                    onSave={(data) => updateHeader(data)}
                    fetching={!!fetchingUpdatePackingOrder}
                  />
                ) : (
                  <LazyLoading height={200} />
                )}
              </DefaultCard>
            </div>
            {/* Información Auditoría */}
            <div className="col-12 col-lg-4">
              <DefaultCard>
                <h5 className="mb-3">Información Auditoría</h5>
                <div className="mb-2">
                  <label className="form-label">Creado por</label>
                  <input className="form-control" value={packingOrder.created_by_name ?? '—'} readOnly disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha de creación</label>
                  <input className="form-control" value={formatDate(packingOrder.created_at)} readOnly disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Editado por</label>
                  <input className="form-control" value={packingOrder.updated_by_name ?? '—'} readOnly disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha de actualización</label>
                  <input className="form-control" value={formatDate(packingOrder.updated_at)} readOnly disabled />
                </div>
              </DefaultCard>
            </div>
          </div>

          {/* Productos Resultados, cantidad requerida */}
          <PackingOrderOutputProductSection
            packingOrderId={packingOrderId}
            outputProducts={outputProducts}
            products={products}
            units={units}
            status={packingOrder.status}
            onReload={() => {
              loadOutputProducts();
              loadRequiredStock();
            }}
          />

          {/* Insumos requeridos + tarjeta Empacar (mismo patrón que Info General + Auditoría) */}
          <div className="row align-items-start">
            <div className="col-12 col-lg-8">
              <PackingOrderInputProductSection
                packingOrderId={packingOrderId}
                requiredStock={requiredStock}
                outputProducts={outputProducts}
                products={products}
                status={packingOrder.status}
                onReload={() => {
                  loadRequiredStock();
                }}
              />
            </div>
            <div className="col-12 col-lg-4">
              <DefaultCard>
                <h5 className="mb-3">Ejecutar Empaque</h5>
                <p className="text-muted small mb-3">
                  Al ejecutar se registrarán los productos elaborados y se descontarán los insumos del inventario.
                </p>
                <div className="d-grid">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    disabled={packingOrder.status !== 'IN_PROGRESS'}
                    title={
                      packingOrder.status !== 'IN_PROGRESS'
                        ? 'Solo se puede empacar con la orden en estado "En progreso".'
                        : 'Registrar empaque'
                    }
                    onClick={() => setShowEmpacarModal(true)}
                  >
                    <i className="mdi mdi-package-variant-closed me-1" />
                    Empacar
                  </button>
                </div>
                {packingOrder.status !== 'IN_PROGRESS' && (
                  <p className="text-muted small mt-2 mb-0 text-center">
                    La orden debe estar en estado <strong>En progreso</strong>.
                  </p>
                )}
              </DefaultCard>
            </div>
          </div>

          {showEmpacarModal && (
            <EmpacarModal
              show={showEmpacarModal}
              packingOrderId={packingOrderId}
              empacarData={empacarData}
              loadingData={!!fetchingEmpacarData}
              loadingExecute={!!fetchingEmpacarExecute}
              onLoadData={loadEmpacarData}
              onSubmit={handleEmpacarSubmit}
              onClose={() => setShowEmpacarModal(false)}
            />
          )}

          {/* Productos elaborados (lotes elaborados por producto) */}
          <div className="mt-3">
            <DefaultCard>
              <h5 className="mb-3">Productos elaborados</h5>
              <p className="text-muted small mb-2">
                Lotes comerciales generados al ejecutar Empacar.
              </p>
              {fetchingProducedBatches ? (
                <LazyLoading height={120} />
              ) : !producedBatches.length ? (
                <p className="text-muted mb-0">Aún no hay lotes elaborados. Use el botón Empacar para registrar producción.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Lote</th>
                        <th>Cantidad</th>
                        <th>Unidad</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {producedBatches.map((row) => (
                        <tr key={row.id}>
                          <td>{row.product_name ?? '—'}</td>
                          <td>
                            <Link to={`/batch/${row.batch_id}/show`}>
                              {row.batch_name ?? `Lote ${row.batch_id}`}
                            </Link>
                          </td>
                          <td>{formatIntQuantity(row.quantity_produced)}</td>
                          <td>{row.unit_name ?? '—'}</td>
                          <td>{row.created_at ? formatDate(row.created_at) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </DefaultCard>
          </div>
        </Tab>

        {/* === TAB 2: Incidentes === */}
        <Tab eventKey="incidents" title="Incidentes">
          <OrderIncidentsTab
            key={reloadTick}
            orderId={packingOrderId}
            onChanged={() => setReloadTick(prev => prev + 1)}
          />
        </Tab>
      </Tabs>
    </>
  );
};

export default PackingOrderEditPage;
